import { useEffect, useState } from "react";
import { ChartLineUp, ArrowSquareOut, ChatCircle, Users, Globe, Storefront, WhatsappLogo } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/settings";

export default function AdminMarketing() {
  const settings = useSettings();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/marketing/stats")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  const gaUrl = settings?.ga4_dashboard_url || "https://analytics.google.com/";

  if (loading) return <div className="text-gos-muted">Loading marketing stats…</div>;
  if (!data) return <div className="text-gos-muted">Stats unavailable.</div>;
  const k = data.kpis;

  return (
    <div data-testid="admin-marketing">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="overline mb-1 flex items-center gap-2"><ChartLineUp size={14} weight="duotone" /> Marketing</div>
          <h2 className="font-display text-2xl text-gos-charcoal">{k.total_leads} total leads · {k.leads_30d} in last 30 days</h2>
          <div className="text-xs text-gos-muted mt-1">Conversion rate (lead/page-view): {k.conversion_rate_pct}%</div>
        </div>
        <a href={gaUrl} target="_blank" rel="noreferrer" data-testid="marketing-ga4-link" className="btn-secondary text-xs !py-2">
          <ArrowSquareOut size={14} weight="bold" /> View Google Analytics
        </a>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi icon={Users} label="Leads (30d)" value={k.leads_30d} testid="kpi-leads-30d" />
        <Kpi icon={ChatCircle} label="Consultations" value={k.consultations} testid="kpi-consult" />
        <Kpi icon={Storefront} label="Franchise apps (30d)" value={k.franchise_apps_30d} testid="kpi-franchise-30d" />
        <Kpi icon={Globe} label="Page views" value={k.page_views_total} testid="kpi-pageviews" />
      </div>

      {/* WhatsApp section — primary conversion */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-5 mb-6" data-testid="whatsapp-panel">
        <div className="flex items-center gap-3 mb-2">
          <WhatsappLogo size={24} weight="duotone" className="text-emerald-700" />
          <div>
            <div className="overline text-emerald-700">WhatsApp leads</div>
            <div className="font-display text-2xl text-gos-charcoal">{k.whatsapp_leads} total · {k.whatsapp_leads_30d} in last 30 days</div>
          </div>
        </div>
        <p className="text-xs text-emerald-900/70">Tracked as a primary conversion event (`whatsapp_click`). Includes leads with `source=whatsapp`, `utm_source=whatsapp`, or `channel=whatsapp`.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Panel title="Top lead sources" rows={data.top_sources} testid="panel-sources" />
        <Panel title="Top countries" rows={data.top_countries} testid="panel-countries" />
        <Panel title="Top UTM sources" rows={data.top_utm_sources} testid="panel-utm-sources" />
        <Panel title="Top campaigns" rows={data.top_campaigns} testid="panel-campaigns" />
      </div>

      <div className="mt-8 text-xs text-gos-muted leading-relaxed">
        Tip: page-view counts come from your backend tracking_events collection (best-effort, frontend-fired).
        For the most accurate visitor numbers, view your live Google Analytics report.
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-gos-border rounded-sm p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} weight="duotone" className="text-gos-navy" />
        <div className="overline">{label}</div>
      </div>
      <div className="font-display text-3xl text-gos-charcoal">{value}</div>
    </div>
  );
}

function Panel({ title, rows, testid }) {
  const entries = Object.entries(rows || {});
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div data-testid={testid} className="bg-white border border-gos-border rounded-sm p-4">
      <div className="overline mb-3">{title}</div>
      {entries.length === 0 && <div className="text-xs text-gos-muted">No data yet.</div>}
      {entries.map(([k, v]) => (
        <div key={k} className="flex items-center justify-between gap-3 py-1">
          <span className="text-sm text-gos-charcoal flex-1 truncate" title={k}>{k}</span>
          <div className="w-40 bg-gos-subtle rounded-sm h-1.5 overflow-hidden">
            <div className="h-full bg-gos-navy" style={{ width: `${(v / max) * 100}%` }} />
          </div>
          <span className="text-xs text-gos-slate w-8 text-right">{v}</span>
        </div>
      ))}
    </div>
  );
}
